import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { updateStockInSubPortfolioTwo, updateRealizedGainV1 } from '../../composables/fetchAPI';

interface SellPartialStockModalProps {
  isOpen: boolean;
  onClose: () => void;
  portfolioId: string;
  subPortfolioId: string;
  stock: any;
  onSave: () => void;
  isSellAll?: boolean; // ✅ เพิ่มรับค่าสถานะว่าเป็นการขายทั้งหมดหรือไม่
}

const SellPartialStockModal: React.FC<SellPartialStockModalProps> = ({
  isOpen,
  onClose,
  portfolioId,
  subPortfolioId,
  stock,
  onSave,
  isSellAll = false,
}) => {
  const [quantity, setQuantity] = useState<number | ''>('');
  const [price, setPrice] = useState<number | ''>('');
  const [isLoading, setIsLoading] = useState(false);

  const currentQty = stock?.quantity || 0;

  useEffect(() => {
    if (isOpen && stock) {
      // ถ้าเป็นการขายทั้งหมด ให้เซตจำนวนหุ้นเป็นยอดทั้งหมดที่มีทันที
      if (isSellAll) {
        setQuantity(currentQty);
      } else {
        setQuantity('');
      }
      setPrice('');
    }
  }, [isOpen, stock, isSellAll, currentQty]);

  if (!isOpen || !stock) return null;

  const avgPrice = stock.avgBuyPrice || stock.avgEntryPrice || 0;
  const currentGain = Number(stock.realizedGain ?? stock.manualRealizedGain ?? 0);
  const stockSymbol = stock.stockSymbol || stock.assetSymbol || stock.symbol;

  const handleSellConfirm = async () => {
    const sellQty = Number(quantity);
    const sellPrice = Number(price);

    if (sellQty <= 0 || isNaN(sellQty)) {
      return toast.error('กรุณาระบุจำนวนหุ้นที่ต้องการขายให้ถูกต้อง');
    }
    if (sellQty > currentQty) {
      return toast.error(`จำนวนหุ้นที่ขาย (${sellQty}) มากกว่าที่มีในพอร์ต (${currentQty})`);
    }
    if (sellPrice <= 0 || isNaN(sellPrice)) {
      return toast.error('กรุณาระบุราคาขายให้ถูกต้อง');
    }

    setIsLoading(true);
    try {
      const remainingQty = currentQty - sellQty;
      const newAvgPrice = remainingQty > 0 ? avgPrice : 0;

      // คำนวณ Realized Gain ที่เพิ่มขึ้นจากการขายครั้งนี้: (ราคาขาย - ต้นทุนเฉลี่ย) * จำนวนหุ้นที่ขาย
      const incrementalGain = (sellPrice - avgPrice) * sellQty;
      const updatedRealizedGain = currentGain + incrementalGain;

      // 1. อัปเดตจำนวนหุ้นและต้นทุนในพอร์ตย่อย
      await updateStockInSubPortfolioTwo(
        portfolioId,
        subPortfolioId,
        stockSymbol,
        {
          stockSymbol: stockSymbol,
          quantity: remainingQty,
          avgBuyPrice: newAvgPrice,
          expectedRatio: remainingQty === 0 ? 0 : (stock.expectedRatio || 0),
        }
      );

      // 2. อัปเดต Realized Gain สะสมใหม่
      await updateRealizedGainV1(portfolioId, subPortfolioId, stockSymbol, {
        realizedGain: updatedRealizedGain,
        action: 'overwrite'
      });

      toast.success(`ขายหุ้น ${stockSymbol} ${isSellAll ? 'ทั้งหมด' : 'บางส่วน'} สำเร็จ!`);
      onSave();
      onClose();
    } catch (error: any) {
      console.error(error);
      toast.error(error?.th_message || error?.message || 'เกิดข้อผิดพลาดในการทำรายการ');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[300] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn">
      <div className="bg-[#18181b] border border-gray-700 p-6 rounded-2xl w-full max-w-md shadow-2xl text-white relative">
        
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className={`text-lg font-bold ${isSellAll ? 'text-orange-400' : 'text-yellow-400'}`}>
            ขายหุ้น {stockSymbol} ({isSellAll ? 'ทั้งหมด' : 'บางส่วน'})
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">✕</button>
        </div>

        {/* Box แสดงข้อมูลหุ้น */}
        <div className="bg-gray-800/60 border border-gray-700 p-4 rounded-xl mb-4 flex justify-between items-center">
          <div>
            <div className="text-xs text-gray-400">จำนวนหุ้นที่มีทั้งหมด</div>
            <div className="text-lg font-black text-white">{currentQty} หุ้น</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-400">ราคาต้นทุนเฉลี่ย</div>
            <div className="text-lg font-black text-yellow-400">${avgPrice.toFixed(2)}</div>
          </div>
        </div>

        {/* Note เตือนสมาชิกตามโจทย์ */}
        <div className="mb-5 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-xs text-yellow-300 leading-relaxed">
          <span className="font-bold">⚠️ คำเตือน:</span> broker Dime การขายบางส่วนในโหมดง่าย อาจทำให้ต้นทุนเฉลี่ยคาดเคลื่อน โปรดใช้การปรับสัดส่วนของหุ้นภายหลังทำรายการใน application 1 - 2 วัน
        </div>

        {/* Form Inputs */}
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-xs font-bold mb-1 opacity-80">
              จำนวนหุ้นที่ขายไป <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="0"
              step="any"
              value={quantity}
              disabled={isSellAll} // ถ้าเป็นขายทั้งหมด ล็อคช่องนี้ไว้ไม่ให้แก้
              onChange={(e) => setQuantity(e.target.value ? Number(e.target.value) : '')}
              placeholder="0.00"
              className={`w-full p-2.5 rounded-lg border border-gray-600 bg-gray-900 text-white font-bold focus:outline-none ${isSellAll ? 'opacity-60 cursor-not-allowed' : 'focus:border-yellow-400'}`}
            />
          </div>

          <div>
            <label className="block text-xs font-bold mb-1 opacity-80">
              ราคาที่ขาย ($) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="0"
              step="any"
              value={price}
              onChange={(e) => setPrice(e.target.value ? Number(e.target.value) : '')}
              placeholder="0.00"
              className="w-full p-2.5 rounded-lg border border-gray-600 bg-gray-900 text-white font-bold focus:outline-none focus:border-yellow-400"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-sm font-bold transition-colors"
          >
            ยกเลิก
          </button>
          <button
            type="button"
            onClick={handleSellConfirm}
            disabled={isLoading}
            className={`px-6 py-2 rounded-lg text-black text-sm font-black transition-transform active:scale-95 shadow-lg disabled:opacity-50 ${isSellAll ? 'bg-orange-500 hover:bg-orange-600 text-white' : 'bg-yellow-400 hover:bg-yellow-500'}`}
          >
            {isLoading ? 'กำลังประมวลผล...' : 'ขาย'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default SellPartialStockModal;